import { listAbnormalEvents } from "./abnormalApi.js";

/**
 * P166: Export, as a real file built from real rows.
 *
 * There is no export endpoint on this backend and this does not pretend there
 * is one. It pages the SAME filtered list the operator is looking at, at the
 * server's own maximum page size, and writes what comes back as CSV. So the
 * file is exactly the list on screen - not the current page of it, and not a
 * separate query that could disagree with it.
 *
 * IT REPORTS ITS OWN LIMIT. Paging stops at MAX_ROWS, and when it does the
 * caller is told how many of how many were written so the message can say so.
 * A truncated export that looks complete is worse than no export: somebody
 * counts the rows in the file and believes it.
 */

// 100 is the server's own Math.Clamp ceiling (AbnormalHeartbeatService).
const PAGE_SIZE = 100;

// Built from its code point rather than typed: a raw U+FEFF in source is an
// invisible character that lint flags and a careless edit silently deletes.
const BOM = String.fromCharCode(0xfeff);

// 50 requests' worth. High enough for every real list this screen shows,
// bounded so a filter that matches everything cannot hammer the server.
export const MAX_ROWS = 5000;

const COLUMNS = [
  ["abnormalCode", "Abnormal Code"],
  ["abnormalType", "Type"],
  ["severity", "Severity"],
  ["status", "Status"],
  ["workerName", "แรงงาน"],
  ["workerCode", "รหัสแรงงาน"],
  ["assignmentCode", "Assignment"],
  ["employerName", "นายจ้าง"],
  ["agentName", "Agent"],
  ["agentCode", "รหัส Agent"],
  ["detectedAtUtc", "Detected At (UTC)"],
  ["lastHeartbeatAtUtc", "Last Heartbeat (UTC)"],
  ["minutesSinceLastHeartbeat", "Missing (min)"],
];

/**
 * One CSV field. Quotes anything containing a comma, a quote or a newline, and
 * doubles embedded quotes - the actual RFC 4180 rule, because a Thai employer
 * name with a comma in it silently shifts every column after it otherwise.
 */
function csvField(value) {
  if (value === null || value === undefined) {
    return "";
  }
  const text = String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function toCsv(items) {
  const header = COLUMNS.map(([, label]) => csvField(label)).join(",");
  const rows = items.map((item) => COLUMNS.map(([key]) => csvField(item[key])).join(","));
  return [header, ...rows].join("\r\n");
}

/** Reads every matching row, up to MAX_ROWS. Returns what it got and whether it stopped early. */
export async function fetchAllAbnormalEvents(filters) {
  const items = [];
  let page = 1;
  let totalCount = 0;

  for (;;) {
    const data = await listAbnormalEvents({ ...filters, page, pageSize: PAGE_SIZE });
    const batch = data?.items ?? [];
    totalCount = data?.totalCount ?? items.length + batch.length;
    items.push(...batch);

    if (batch.length < PAGE_SIZE || items.length >= totalCount || items.length >= MAX_ROWS) {
      break;
    }
    page += 1;
  }

  return { items: items.slice(0, MAX_ROWS), totalCount, truncated: totalCount > MAX_ROWS };
}

/**
 * Hands the browser a file. A BOM leads the content on purpose: without it
 * Excel on Windows reads a UTF-8 CSV as the system codepage and every Thai
 * name in the file arrives as mojibake - which is the whole point of exporting
 * this particular list.
 */
export function downloadCsv(csv, fileName) {
  if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
    return false;
  }
  const blob = new Blob([BOM, csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL?.(url), 60_000);
  return true;
}
